import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
	CLI_VERIFICATION_CHANNEL,
	CliNumberValidationResponse,
	CliVerificationAttemptResponse,
	CliVerificationManualRequestRequest,
	CliVerificationManualRequestResponse,
	CliVerificationTriggerRequest,
	CliVerificationTriggerResponse,
} from '../models/cli-verification.model';

export const CLI_VALIDATE_TIMEOUT_MS = 10_000;
/**
 * Longer than the other calls: the server places the outbound call before it answers. A timeout
 * does not mean no call was placed, and re-triggering is safe - the server hands back the same
 * attempt and code while that call runs.
 */
export const CLI_TRIGGER_TIMEOUT_MS = 20_000;
export const CLI_POLL_TIMEOUT_MS = 10_000;
export const CLI_CANCEL_TIMEOUT_MS = 10_000;
export const CLI_MANUAL_REQUEST_TIMEOUT_MS = 10_000;

/**
 * Talks to phoneservice's own-number verification with the customer's bearer.
 *
 * `phoneServiceId` is always the one resolved from the token, passed in by the caller - never read
 * from the callnote or the URL, for the same reason as the SIP order.
 */
@Injectable({
	providedIn: 'root',
})
export class CliVerificationService {
	private readonly httpClient: HttpClient = inject(HttpClient);

	validate$(phoneServiceId: number, phoneNumber: string): Observable<CliNumberValidationResponse> {
		return this.httpClient
			.post<CliNumberValidationResponse>(this.url('/number/validate'), { phoneNumber }, { params: params(phoneServiceId) })
			.pipe(timeout(CLI_VALIDATE_TIMEOUT_MS));
	}

	trigger$(phoneServiceId: number, phoneNumber: string, declarationText: string): Observable<CliVerificationTriggerResponse> {
		const body: CliVerificationTriggerRequest = { phoneNumber, channel: CLI_VERIFICATION_CHANNEL, declarationText };
		return this.httpClient
			.post<CliVerificationTriggerResponse>(this.url('/attempts'), body, { params: params(phoneServiceId) })
			.pipe(timeout(CLI_TRIGGER_TIMEOUT_MS));
	}

	poll$(phoneServiceId: number, attemptId: number): Observable<CliVerificationAttemptResponse> {
		return this.httpClient
			.get<CliVerificationAttemptResponse>(this.url(`/attempts/${attemptId}`), { params: params(phoneServiceId) })
			.pipe(timeout(CLI_POLL_TIMEOUT_MS));
	}

	/** Cancelling an attempt that already ended returns the state it ended in - not necessarily CANCELLED. */
	cancel$(phoneServiceId: number, attemptId: number): Observable<CliVerificationAttemptResponse> {
		return this.httpClient
			.post<CliVerificationAttemptResponse>(this.url(`/attempts/${attemptId}/cancel`), null, { params: params(phoneServiceId) })
			.pipe(timeout(CLI_CANCEL_TIMEOUT_MS));
	}

	requestManual$(phoneServiceId: number, phoneNumber: string, note: string): Observable<CliVerificationManualRequestResponse> {
		const body: CliVerificationManualRequestRequest = { phoneNumber, note };
		return this.httpClient
			.post<CliVerificationManualRequestResponse>(this.url('/manual-requests'), body, { params: params(phoneServiceId) })
			.pipe(timeout(CLI_MANUAL_REQUEST_TIMEOUT_MS));
	}

	private url(path: string): string {
		return `${environment.baseUrl}/phoneservice/v3/cli-verification${path}`;
	}
}

function params(phoneServiceId: number): HttpParams {
	return new HttpParams().set('phoneServiceId', phoneServiceId);
}
